import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { validateApiResponse } from '../../../services';
import {
  getMemberStudioGrants,
  grantMemberStudio,
  listStudios,
  removeMemberStudioAccess,
  saveMemberStudioGrants,
} from '../../../services/studiosService';
import { MEMBER_STATUS, USER_TYPE, USER_TYPE_META } from '../constants/copy';
import { PRIVILEGED_ACCESS_LEVEL } from '../constants/permissions';
import { getDescendants } from '../utils/permissionTree';

const DIRECT_SOURCE = Object.freeze({ type: 'direct', label: 'direct' });

/** Catalog studio + the member's grants → the studio row the tab renders. */
const mapApiStudioToUi = (studio, access) => {
  const grants = access?.grants ?? [];
  const roleNames = access?.role_names ?? [];
  const direct = Boolean(access?.direct_access);
  return {
    id: studio.id,
    name: studio.name,
    description: studio.description,
    icon: studio.icon,
    status: access?.status ?? 'active',
    tree: studio.permissions,
    grants,
    sources: [...roleNames.map((label) => ({ type: 'role', label })), ...(direct ? [DIRECT_SOURCE] : [])],
    otherMemberCount: access?.other_member_count ?? 0,
    counts: { direct: access?.counts?.added_directly ?? 0, fromRoles: access?.counts?.from_roles ?? 0 },
    isGranted: grants.length > 0 || direct,
    isDirectOnly: direct && roleNames.length === 0,
    hasRoleAccess: roleNames.length > 0,
    canEdit: true,
  };
};

/**
 * Admin / Owner reach every studio by type: the tag is the only source and nothing is editable.
 * The read view lists every permission as inherited from the type ("via Admin"), at the widest
 * level.
 */
const mapPrivilegedStudio = (studio, userType, typeLabel) => {
  const via_roles = [{ id: userType, name: typeLabel }];
  const grants = studio.permissions
    .flatMap((node) => [node, ...getDescendants(node)])
    .map((node) => ({ permission_id: node.id, level: PRIVILEGED_ACCESS_LEVEL, source: 'role', via_roles }));
  return {
    ...mapApiStudioToUi(studio, null),
    grants,
    sources: [{ type: userType, label: userType }],
    isGranted: true,
    canEdit: false,
  };
};

const isPrivileged = (userType) => userType === USER_TYPE.ADMIN || userType === USER_TYPE.OWNER;

/**
 * Studios of a member: `{ studios, grantableStudios, isLoading, error, refetch }` plus the
 * mutations `save(studioId, grants)`, `grant(studioId)` and `removeAccess(studioId)`. Each
 * mutation resolves `true`/`false`, toasts the outcome and refetches on success. The list
 * refetches by itself when the member's roles or user type change (direct grants survive).
 *
 * @param {object} member UI member (see `mapApiMemberToUi`)
 */
export const useMemberStudios = (member) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [state, setState] = useState({ catalog: [], isLoading: true, error: null });
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  const { id: memberId, userType } = member;
  const isSuspended = member.status === MEMBER_STATUS.SUSPENDED;
  const roleKey = member.roleIds.join(',');

  const load = useCallback(({ silent = false } = {}) => {
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    return Promise.all([listStudios(), getMemberStudioGrants(memberId)])
      .then(([catalog, access]) => [
        validateApiResponse(catalog, t('studios.loadError')).body.items,
        validateApiResponse(access, t('studios.loadError')).body.items,
      ])
      .then(([items, accessItems]) => {
        const privileged = isPrivileged(userType);
        const catalog = items.map((studio) =>
          privileged
            ? mapPrivilegedStudio(studio, userType, t(USER_TYPE_META[userType].labelKey))
            : mapApiStudioToUi(studio, accessItems.find((a) => a.studio_id === studio.id)),
        );
        return { catalog, isLoading: false, error: null };
      })
      .catch(() => null)
      .then((next) => {
        const isCurrent = mountedRef.current && fetchId === fetchIdRef.current;
        // A silent refetch (after a save, grant or remove) that fails keeps the studios shown.
        if (isCurrent && (next || !silent)) {
          setState(next ?? { catalog: [], isLoading: false, error: 'failed' });
        }
        return Boolean(next);
      });
  }, [memberId, userType, t]);

  // `roleKey` makes a change of the member's roles refetch (direct grants survive it).
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load, roleKey]);

  const mutate = useCallback(
    async (request, successKey, studioId) => {
      const studioName = state.catalog.find((s) => s.id === studioId)?.name ?? studioId;
      try {
        validateApiResponse(await request(), t('studios.toast.failed'));
        const refreshed = await load({ silent: true });
        toastRef.current.success({
          title: t(`studios.toast.${successKey}.title`),
          message: t(`studios.toast.${successKey}.message`, { name: member.displayName, studio: studioName }),
        });
        if (!refreshed) {
          toastRef.current.error({
            title: t('studios.toast.refreshFailed.title'),
            message: t('studios.toast.refreshFailed.message'),
          });
        }
        return true;
      } catch (err) {
        toastRef.current.error({ title: t('studios.toast.failed'), message: err?.message ?? t('memberDetail.toast.unknown') });
        return false;
      }
    },
    [state.catalog, load, member.displayName, t],
  );

  const save = useCallback(
    (studioId, grants) =>
      mutate(() => saveMemberStudioGrants(memberId, { studio_id: studioId, grants }), 'saved', studioId),
    [mutate, memberId],
  );
  const grant = useCallback((studioId) => mutate(() => grantMemberStudio(memberId, studioId), 'granted', studioId), [mutate, memberId]);
  const removeAccess = useCallback(
    (studioId) => mutate(() => removeMemberStudioAccess(memberId, studioId), 'removed', studioId),
    [mutate, memberId],
  );

  return useMemo(() => {
    const { isLoading, error } = state;
    // A suspended member has no effective access: the studios stay listed, read-only (CT-5827).
    const catalog = isSuspended
      ? state.catalog.map((studio) => ({ ...studio, status: MEMBER_STATUS.SUSPENDED, isSuspended: true }))
      : state.catalog;
    return {
      isSuspended,
      studios: catalog.filter((studio) => studio.isGranted),
      grantableStudios: catalog.filter((studio) => !studio.isGranted),
      isLoading,
      error,
      save,
      grant,
      removeAccess,
      refetch: load,
    };
  }, [state, isSuspended, save, grant, removeAccess, load]);
};
